/**
 * Unified Conversation Manager
 * 
 * Simplifies conversation state management to maintain single conversation flow
 * instead of creating new conversations for every query type.
 */

import { Conversation, ChatMessage } from '../api';

export interface ConversationManagerState {
  currentConversation: Conversation | null;
  shouldContinueConversation: boolean;
}

export class ConversationManager {
  private state: ConversationManagerState = {
    currentConversation: null,
    shouldContinueConversation: true
  };

  /**
   * Get current conversation or create one if needed
   * This ensures we maintain conversation continuity throughout topics
   */
  getCurrentOrCreateConversation(userMessage: ChatMessage, title?: string): Conversation {
    // If we have an existing conversation and should continue it, add message to it
    if (this.state.currentConversation && this.state.shouldContinueConversation) {
      const updatedConversation = {
        ...this.state.currentConversation,
        messages: [...this.state.currentConversation.messages, userMessage]
      };
      this.state.currentConversation = updatedConversation;
      return updatedConversation;
    }

    // Create new conversation only when explicitly needed
    const newConversation: Conversation = {
      id: '', // Let backend assign the ID - don't use uuid() here!
      title: title || this.generateTitle(userMessage.content),
      messages: [userMessage],
      date: new Date().toISOString(),
      fromBackend: false
    };

    this.state.currentConversation = newConversation;
    return newConversation;
  }

  /**
   * Update conversation with backend-assigned ID
   */
  updateConversationId(backendId: string): void {
    if (this.state.currentConversation) {
      this.state.currentConversation = {
        ...this.state.currentConversation,
        id: backendId,
        fromBackend: true
      };
    }
  }

  /**
   * Add message to current conversation
   */
  addMessage(message: ChatMessage): void {
    if (this.state.currentConversation) {
      this.state.currentConversation.messages.push(message);
    }
  }

  /**
   * Get current conversation
   */
  getCurrentConversation(): Conversation | null {
    return this.state.currentConversation;
  }

  /**
   * Set current conversation (when loading from history)
   */
  setCurrentConversation(conversation: Conversation | null): void {
    this.state.currentConversation = conversation;
    this.state.shouldContinueConversation = conversation !== null;
  }

  /**
   * Force start new conversation (for explicit "new chat" action)
   */
  startNewConversation(): void {
    this.state.currentConversation = null;
    this.state.shouldContinueConversation = true;
  }

  /**
   * Check if we should continue current conversation or start new one
   */
  shouldContinueCurrentConversation(): boolean {
    return this.state.shouldContinueConversation && this.state.currentConversation !== null;
  }

  /**
   * Generate title from message content
   */
  private generateTitle(content: ChatMessage['content']): string {
    const cleanContent = typeof content === 'string' ? content : JSON.stringify(content);
    return cleanContent.substring(0, 50) + (cleanContent.length > 50 ? "..." : "");
  }

  /**
   * Enable/disable conversation continuation
   */
  setContinuationMode(shouldContinue: boolean): void {
    this.state.shouldContinueConversation = shouldContinue;
  }
}

// Export singleton instance for consistent state across components
export const conversationManager = new ConversationManager();